<template>
  <div class="grid">
    <div class="col-12 md:col-8 md:col-offset-2 lg:col-6 lg:col-offset-3">
      <div class="card">
        <h5>Practice Vocabulary</h5>

        <div>
          <div
            v-if="loading"
            class="flex flex-column align-items-center justify-content-center gap-3 py-6"
          >
            <i
              class="pi pi-spin pi-spinner text-primary"
              style="font-size: 3rem"
            />
            <span class="text-600">Loading next question…</span>
          </div>

          <div
            v-else-if="!currentQuestion && !result"
            class="flex flex-column align-items-center gap-3"
          >
            <div class="field w-full">
              <label for="group">Group Filter (Optional)</label>
              <Dropdown
                id="group"
                v-model="selectedGroup"
                :options="groups"
                option-label="title"
                placeholder="Select a group to practice"
                fluid
              />
              <small>Leave empty to practice all groups</small>
            </div>

            <div class="field w-full">
              <label for="options">Answer options</label>
              <Dropdown
                id="options"
                v-model="answerOptions"
                :options="optionChoices"
                fluid
              />
              <small>How many answers to choose from (4–8)</small>
            </div>

            <div class="field w-full">
              <div class="flex align-items-center gap-2">
                <Checkbox
                  input-id="autoContinue"
                  v-model="autoContinue"
                  :binary="true"
                />
                <label
                  for="autoContinue"
                  class="mb-0"
                >Auto-continue to next question</label>
              </div>
            </div>

            <div
              v-if="autoContinue"
              class="field w-full"
            >
              <label for="autoContinueSeconds">Auto-continue delay (seconds)</label>
              <InputNumber
                id="autoContinueSeconds"
                v-model="autoContinueSeconds"
                :min="1"
                :max="60"
                show-buttons
                fluid
              />
              <small>How long the result is shown before advancing</small>
            </div>

            <Button
              label="Start Practice"
              icon="pi pi-play"
              @click="startPractice"
            />
          </div>

          <div v-else-if="currentQuestion && !result">
            <div class="text-center mb-4">
              <h3
                v-if="selectedGroup"
                class="mb-2 text-primary"
              >
                Group: {{ selectedGroup.title }}
              </h3>
              <h2 class="flex align-items-center justify-content-center">
                <i
                  class="pi pi-volume-up mr-2 cursor-pointer"
                  style="font-size: 1.5rem"
                  @click="speak"
                />
                {{ currentQuestion.question }}
              </h2>
            </div>

            <div class="grid">
              <div
                v-for="answer in currentQuestion.answers"
                :key="answer.id"
                class="col-12"
              >
                <Button
                  :label="answer.answer"
                  class="p-button-outlined p-button-secondary w-full"
                  @click="checkAnswer(answer.id)"
                />
              </div>
            </div>

            <div class="flex justify-content-center mt-4">
              <Button
                label="Stop"
                icon="pi pi-stop"
                severity="secondary"
                text
                @click="stopPractice"
              />
            </div>
          </div>

          <div
            v-else-if="result"
            class="flex justify-content-center align-items-center flex-column"
          >
            <div
              v-if="result.is_valid"
              class="text-center"
            >
              <i
                class="pi pi-check-circle text-green-500"
                style="font-size: 6rem"
              />
              <h3 class="text-green-500">
                Correct!
              </h3>
            </div>
            <div
              v-else
              class="text-center"
            >
              <i
                class="pi pi-times-circle text-red-500"
                style="font-size: 6rem"
              />
              <h3 class="text-red-500">
                Incorrect!
              </h3>
            </div>

            <p
              v-if="autoContinue"
              class="text-600 mt-3 mb-0"
            >
              Auto-continuing in {{ countdown }}s…
            </p>

            <div class="flex gap-2 mt-4">
              <Button
                label="Continue"
                icon="pi pi-arrow-right"
                @click="nextQuestion"
              />
              <Button
                label="Stop"
                icon="pi pi-stop"
                severity="secondary"
                text
                @click="stopPractice"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { questionsService } from '../service'

export default {
  data () {
    return {
      currentQuestion: null,
      result: null,
      synthesis: null,
      selectedGroup: null,
      answerOptions: 4,
      optionChoices: [4, 5, 6, 7, 8],
      loading: false,
      autoContinue: false,
      autoContinueSeconds: 3,
      autoContinueTimer: null,
      countdown: 0
    }
  },
  computed: {
    ...mapState({
      userId: state => state.auth.user?.id,
      groups: state => state.groups.items
    })
  },
  mounted () {
    // Initialize speech synthesis
    this.synthesis = window.speechSynthesis

    // Fetch groups if not already loaded
    if (!this.groups.length) {
      this.$store.dispatch('groups/getGroups')
    }
  },
  beforeUnmount () {
    this.clearAutoContinue()
  },
  methods: {
    startPractice () {
      this.getNextQuestion()
    },

    getNextQuestion () {
      this.loading = true

      // Get the group ID if a group is selected
      const groupId = this.selectedGroup ? this.selectedGroup.id : null

      questionsService.getQuestion(groupId, this.answerOptions)
        .then(response => {
          this.currentQuestion = response.data
          this.loading = false
        })
        .catch(error => {
          console.error('Error fetching question:', error)
          this.loading = false
        })
    },

    checkAnswer (answerId) {
      if (!this.currentQuestion) return

      const data = {
        question_id: this.currentQuestion.id,
        answer_id: answerId
      }

      questionsService.postAnswer(data)
        .then(response => {
          this.result = response.data
          if (this.autoContinue) {
            this.startAutoContinue()
          }
        })
        .catch(error => {
          console.error('Error checking answer:', error)
        })
    },

    startAutoContinue () {
      this.clearAutoContinue()
      this.countdown = Math.max(1, Number(this.autoContinueSeconds) || 1)
      this.autoContinueTimer = setInterval(() => {
        this.countdown -= 1
        if (this.countdown <= 0) {
          this.nextQuestion()
        }
      }, 1000)
    },

    clearAutoContinue () {
      if (this.autoContinueTimer) {
        clearInterval(this.autoContinueTimer)
        this.autoContinueTimer = null
      }
      this.countdown = 0
    },

    nextQuestion () {
      this.clearAutoContinue()
      this.result = null
      this.currentQuestion = null
      this.getNextQuestion()
    },

    stopPractice () {
      this.clearAutoContinue()
      this.result = null
      this.currentQuestion = null
      this.loading = false
    },

    speak () {
      if (!this.currentQuestion || !this.synthesis) return

      const utterance = new SpeechSynthesisUtterance(this.currentQuestion.question)
      this.synthesis.speak(utterance)
    }
  }
}
</script>
